import { findNestedKey, normalizeStoreNumber } from "helpers";

/*
 * Storefront inventory for a variant at the preferred store.
 *
 * Collection cards each call `getAvailability()` (add-to-cart, quick-add, etc.).
 * Concurrent misses for different variants are queued and flushed as one
 * `nodes(ids:)` GraphQL request per 25 variants per store, instead of one POST
 * per card. Same variant+store reuses an in-flight or settled Promise.
 *
 * "Flush" here means drain `pending` and send GraphQL — not flush a single
 * 25-id chunk. `scheduleFlush` books one drain (microtask, or DOMContentLoaded
 * if the document is still parsing). `processPending` is that drain; it may
 * call `fetchBatch` more than once.
 *
 * Shared on `WYATT.StoreAvailability`:
 * - cache: Map of `${variantId}:${storeNumber}` → Promise of StoreAvailabilityEdge[]
 * - pending: FlushJob[]
 * - flushScheduled: boolean — a drain is already booked for this wave
 *
 * FlushJob:
 * {
 *   variant: number,
 *   storeNumber: number,
 *   near: { latitude: number, longitude: number } | null,
 *   instance: StoreAvailability,
 *   resolve: (edges: StoreAvailabilityEdge[]) => void,
 *   reject: (error: Error) => void,
 * }
 *
 * StoreAvailabilityEdge (raw GraphQL edge, before `processLocation`):
 * {
 *   node: {
 *     location: {
 *       id: "gid://shopify/Location/123",
 *       name: string,
 *       storeNumber: { value: string } | null, // ethel.erp_id JSON, e.g. "266"
 *     },
 *     quantityAvailable: number,
 *   },
 * }
 *
 * Batched GraphQL body: `{ data: { nodes: Array<{ id, storeAvailability }> } }`.
 * Paginated follow-up: `{ data: { node: { storeAvailability } } }`.
 * storeAvailability: `{ edges: StoreAvailabilityEdge[], pageInfo: { hasNextPage, endCursor } }`.
 */

// Caps `nodes(ids:)` size / Storefront query cost. Extra variants become another POST.
const BATCH_SIZE = 25;

function variantGid(variant) {
  return `gid://shopify/ProductVariant/${Number(variant)}`;
}

function chunk(items, size) {
  const chunks = [];

  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }

  return chunks;
}

function globalStoreAvailabilityState() {
  WYATT.StoreAvailability ??= {};
  WYATT.StoreAvailability.cache ??= new Map();
  WYATT.StoreAvailability.pending ??= [];
  WYATT.StoreAvailability.flushScheduled ??= false;

  return WYATT.StoreAvailability;
}

/** Pull storeAvailability for one variant out of a nodes or node GraphQL payload. */
function storeAvailabilityForVariant(parsed, variant) {
  const gid = variantGid(variant);
  const nodes = parsed?.data?.nodes;

  if (Array.isArray(nodes)) {
    const match = nodes.find((node) => node?.id === gid);

    if (match) {
      return match.storeAvailability;
    }
  }

  return (
    parsed?.data?.node?.storeAvailability ?? {
      edges: [],
      pageInfo: {},
    }
  );
}

/**
 * Book a single flush of `pending`. A flush drains the queue (`processPending`)
 * and fires the batched Storefront POSTs. Later enqueues in the same turn see
 * `flushScheduled` and only push onto `pending`; they do not schedule another
 * drain. Wait for DOMContentLoaded if the document is still parsing so cards
 * that upgrade later can join this wave.
 */
function scheduleFlush(flush) {
  const state = globalStoreAvailabilityState();

  if (state.flushScheduled) {
    return;
  }

  state.flushScheduled = true;

  const run = () => {
    state.flushScheduled = false;
    flush();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
    return;
  }

  queueMicrotask(run);
}

class StoreAvailability {
  AVAILABILITY_STATE = Object.freeze({
    AVAILABLE: "available",
    INSUFFICIENT: "insufficient",
    OUT_OF_STOCK: "out-of-stock",
    SELECT_STORE: "select-store",
  });

  RESPONSE_KEYS = {
    AVAILABILITIES: "storeAvailability",
    LOCATION: "location",
    QUANTITY_AVAILABLE: "quantityAvailable",
  };

  // Processed location rows after a fetch:
  // { id: number, name: string, quantityAvailable: number, storeNumber: number | null }
  locations = [];

  constructor(host) {
    this.host = host;
  }

  get config() {
    return this.host.config;
  }

  get storeNumber() {
    if (this.config.storeNumber != null) {
      return normalizeStoreNumber(this.config.storeNumber);
    }

    if (this.host.dataset.storeNumber) {
      return normalizeStoreNumber(this.host.dataset.storeNumber);
    }

    return normalizeStoreNumber(WYATT.PreferredStores?.store?.store_number);
  }

  get nearCoordinates() {
    const store =
      this.storeMatchingPreferredNumber(WYATT.PreferredStores?.store) ??
      WYATT.shopify?.stores?.find((candidate) =>
        this.storeMatchingPreferredNumber(candidate),
      );

    const latitude = store?.address?.latitude;
    const longitude = store?.address?.longitude;

    if (latitude == null || longitude == null) {
      return null;
    }

    return {
      latitude: Number(latitude),
      longitude: Number(longitude),
    };
  }

  get quantityAvailable() {
    return this.locations.find(
      (location) => location.storeNumber === this.storeNumber,
    )?.quantityAvailable;
  }

  get availability() {
    if (!this.storeNumber) {
      return this.AVAILABILITY_STATE.SELECT_STORE;
    }

    const matchedLocation = this.locations.find(
      (loc) => loc.storeNumber === this.storeNumber,
    );

    if (!matchedLocation) {
      return this.AVAILABILITY_STATE.OUT_OF_STOCK;
    }

    if (matchedLocation.quantityAvailable <= 0) {
      return this.AVAILABILITY_STATE.OUT_OF_STOCK;
    }

    if (matchedLocation.quantityAvailable < this.config.quantity) {
      return this.AVAILABILITY_STATE.INSUFFICIENT;
    }

    return this.AVAILABILITY_STATE.AVAILABLE;
  }

  get isAvailable() {
    return this.availability === this.AVAILABILITY_STATE.AVAILABLE;
  }

  cacheKey(variant, storeNumber) {
    return `${Number(variant)}:${storeNumber}`;
  }

  storeMatchingPreferredNumber(store) {
    if (!store) {
      return null;
    }

    if (normalizeStoreNumber(store.store_number) !== this.storeNumber) {
      return null;
    }

    return store;
  }

  edgeMatchesPreferredStore(edge, storeNumber) {
    return this.processLocation(edge).storeNumber === storeNumber;
  }

  async getAvailability() {
    if (!this.storeNumber) {
      return this.AVAILABILITY_STATE.SELECT_STORE;
    }

    const key = this.cacheKey(this.config.variant, this.storeNumber);
    const state = globalStoreAvailabilityState();
    const { cache } = state;

    // Store the Promise (not the edges) so concurrent callers share one fetch.
    if (!cache.has(key)) {
      cache.set(key, this.enqueueBatch(this.config.variant, this.storeNumber));
    }

    try {
      const rawLocations = await cache.get(key);
      this.locations = rawLocations.map(this.processLocation.bind(this));

      return this.availability;
    } catch (error) {
      cache.delete(key);
      throw error;
    }
  }

  enqueueBatch(variant, storeNumber) {
    const state = globalStoreAvailabilityState();

    return new Promise((resolve, reject) => {
      state.pending.push({
        variant: Number(variant),
        storeNumber,
        near: this.nearCoordinates,
        instance: this,
        resolve,
        reject,
      });

      scheduleFlush(() => {
        this.processPending();
      });
    });
  }

  // The scheduled flush: take every pending job, then fetch in BATCH_SIZE chunks.
  async processPending() {
    const state = globalStoreAvailabilityState();
    const jobs = state.pending;
    state.pending = [];

    if (jobs.length === 0) {
      return;
    }

    const byStore = new Map();

    for (const job of jobs) {
      const groupKey = String(job.storeNumber);
      const group = byStore.get(groupKey) ?? [];
      group.push(job);
      byStore.set(groupKey, group);
    }

    // One GraphQL `nodes` query per store, chunked to BATCH_SIZE variant IDs.
    const batches = [...byStore.values()].flatMap((group) =>
      chunk(group, BATCH_SIZE),
    );

    await Promise.all(batches.map((batch) => this.fetchBatch(batch)));
  }

  async fetchBatch(jobs) {
    try {
      const parsed = await this.fetchingNodes(
        jobs.map((job) => job.variant),
        jobs[0].near,
      );

      await Promise.all(jobs.map((job) => this.resolveBatchJob(job, parsed)));
    } catch (error) {
      for (const job of jobs) {
        job.reject(error);
      }
    }
  }

  async resolveBatchJob(job, parsed) {
    try {
      const storeAvailability = storeAvailabilityForVariant(
        parsed,
        job.variant,
      );
      const edges = storeAvailability?.edges ?? [];
      const { pageInfo } = storeAvailability ?? {};

      if (
        edges.some((edge) =>
          job.instance.edgeMatchesPreferredStore(edge, job.storeNumber),
        )
      ) {
        job.resolve(edges);
        return;
      }

      const nextCursor = pageInfo?.hasNextPage ? pageInfo.endCursor : null;

      if (!nextCursor) {
        job.resolve(edges);
        return;
      }

      // Preferred store not in the first 50 nearby locations; paginate this variant only.
      const nextEdges = await job.instance.fetchingPage(
        job.variant,
        job.storeNumber,
        nextCursor,
      );

      job.resolve([...edges, ...nextEdges]);
    } catch (error) {
      job.reject(error);
    }
  }

  processLocation({ node }) {
    const { location, quantityAvailable } = node;
    const { id, name, storeNumber } = location;

    return {
      id: JSON.parse(id.split("/Location/")[1]),
      name,
      quantityAvailable,
      storeNumber: storeNumber?.value
        ? normalizeStoreNumber(JSON.parse(storeNumber.value))
        : null,
    };
  }

  nearArg(near) {
    if (!near) {
      return "";
    }

    return `, near: { latitude: ${near.latitude}, longitude: ${near.longitude} }`;
  }

  storeAvailabilitySelection(after, near) {
    const pageSize = 50;
    const afterArg = after ? `, after: "${after}"` : "";

    return `storeAvailability(first: ${pageSize}${afterArg}${this.nearArg(near)}) {
                  edges {
                    node {
                      location {
                        id
                        name
                        storeNumber: metafield(namespace: "ethel", key: "erp_id") {
                          value
                        }
                      }
                      quantityAvailable
                    }
                  }
                  pageInfo {
                    hasNextPage
                    endCursor
                  }
                }`;
  }

  /**
   * Storefront `nodes(ids:)` — one POST for many ProductVariant GIDs.
   * `near` is copied onto each `storeAvailability` field so locations sort by
   * distance to the preferred store.
   */
  async fetchingNodes(variants, near) {
    const ids = variants
      .map((variant) => `"${variantGid(variant)}"`)
      .join(", ");
    const options = {
      method: "post",
      headers: WYATT.config.api.headers,
      body: JSON.stringify({
        query: `{
            nodes(ids: [${ids}]) {
              id
              ... on ProductVariant {
                ${this.storeAvailabilitySelection(null, near)}
              }
            }
          }`,
      }),
    };

    return this.requestGraphql(options);
  }

  /** Single-variant `node(id:)` + `after` cursor when the batched page missed the store. */
  async fetchingPage(variant, storeNumber, after) {
    const options = {
      method: "post",
      headers: WYATT.config.api.headers,
      body: JSON.stringify({
        query: `{
            node(id: "${variantGid(variant)}") {
              ... on ProductVariant {
                ${this.storeAvailabilitySelection(after, this.nearCoordinates)}
              }
            }
          }`,
      }),
    };

    const parsedResponse = await this.requestGraphql(options);
    const storeAvailability = findNestedKey(
      parsedResponse,
      this.RESPONSE_KEYS.AVAILABILITIES,
    );

    const edges = storeAvailability?.edges ?? [];
    const { pageInfo } = storeAvailability ?? {};

    if (
      edges.some((edge) => this.edgeMatchesPreferredStore(edge, storeNumber))
    ) {
      return edges;
    }

    const nextCursor = pageInfo?.hasNextPage ? pageInfo.endCursor : null;

    if (!nextCursor) {
      return edges;
    }

    const nextEdges = await this.fetchingPage(variant, storeNumber, nextCursor);
    return [...edges, ...nextEdges];
  }

  async requestGraphql(options) {
    const response = await fetch(WYATT.config.api.endpoint, options);

    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }

    const parsed = await response.json();

    if (parsed.errors?.length) {
      throw new Error(parsed.errors[0].message ?? "GraphQL error");
    }

    return parsed;
  }
}

export default StoreAvailability;
